// The base no-unused-vars rule does not see type-only uses.
/* eslint no-unused-vars: "off", "@typescript-eslint/no-unused-vars": "warn" */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  Flashbar,
  Header,
  SpaceBetween,
  StatusIndicator,
  Table,
} from '@amzn/awsui-components-react-v3/polaris';
import ApiClient from '../../../ApiClient';
import { RetryAlert } from '../../Common/ErrorMessages';
import { convertDateTimePretty, valueOrDash } from '../../../utils/ReadableConverter';
import { ApiError } from '../../../utils/serverErrors';
import * as goldenDatasets from './goldenDatasets';

interface GoldenDatasetsListProps {
  apiClient: {
    httpGet: (path: string) => Promise<unknown>;
  };
  navigate: (path: string) => void;
  canWrite: boolean;
  // The name of the dataset just deleted, to confirm the delete.
  deletedName?: string;
}

type Summary = goldenDatasets.GoldenDatasetSummary;

const columnDefinitions = [
  {
    id: 'name',
    header: 'Name',
    cell: (dataset: Summary): JSX.Element => (
      <Link to={goldenDatasets.datasetRoute(dataset.datasetId)}>{dataset.name}</Link>
    ),
  },
  {
    id: 'description',
    header: 'Description',
    cell: (dataset: Summary): string => valueOrDash(dataset.description),
  },
  {
    id: 'pricingModel',
    header: 'Pricing model',
    cell: (dataset: Summary): string => dataset.pricingModelName,
  },
  {
    id: 'scopingModel',
    header: 'Scoping model',
    cell: (dataset: Summary): string => dataset.scopingModelName,
  },
  { id: 'rows', header: 'Rows', cell: (dataset: Summary): number => dataset.rowCount },
  { id: 'version', header: 'Version', cell: (dataset: Summary): number => dataset.version },
  {
    id: 'lastChanged',
    header: 'Last changed',
    cell: (dataset: Summary): string =>
      `${convertDateTimePretty(dataset.updatedAt)} by ${dataset.author}`,
  },
  {
    id: 'costImpact',
    header: 'Cost impact',
    cell: (dataset: Summary): React.ReactNode =>
      goldenDatasets.skippingWarnings(dataset).length > 0 && (
        <StatusIndicator type="warning">{goldenDatasets.SKIPPED_IN_IMPACT}</StatusIndicator>
      ),
  },
];

/** The organization's golden datasets in Pricing's order, by name. */
export const GoldenDatasetsList = ({
  apiClient,
  navigate,
  canWrite,
  deletedName,
}: GoldenDatasetsListProps): JSX.Element => {
  const [datasets, setDatasets] = useState<Summary[]>([]);
  const [deletedShown, setDeletedShown] = useState(Boolean(deletedName));
  const [loading, setLoading] = useState(true);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);

  const load = useCallback((): void => {
    setLoading(true);
    setLoadErrors([]);
    apiClient
      .httpGet(goldenDatasets.GOLDEN_DATASETS_PATH)
      .then(response => setDatasets(response as Summary[]))
      .catch((error: ApiError) => {
        setDatasets([]);
        setLoadErrors(goldenDatasets.describeError(error));
      })
      .finally(() => setLoading(false));
  }, [apiClient]);

  useEffect(load, [load]);

  const createPath = `${goldenDatasets.GOLDEN_DATASETS_ROUTE}/new`;

  return (
    <SpaceBetween size="m">
      {deletedShown && (
        <Flashbar
          id="goldenDatasetDeleted"
          items={[
            {
              type: 'success',
              content: `Deleted ${deletedName}`,
              dismissible: true,
              onDismiss: (): void => setDeletedShown(false),
            },
          ]}
        />
      )}
      {loadErrors.length > 0 && (
        <RetryAlert
          id="goldenDatasetsLoadError"
          header="Could not load golden datasets"
          messages={loadErrors}
          retryId="retryGoldenDatasets"
          onRetry={load}
        />
      )}
      <Table
        id="goldenDatasetsTable"
        items={datasets}
        loading={loading}
        loadingText="Loading golden datasets"
        trackBy="datasetId"
        columnDefinitions={columnDefinitions}
        wrapLines
        header={
          <Header
            variant="h2"
            actions={
              canWrite && (
                <Button
                  id="createGoldenDataset"
                  variant="primary"
                  href={createPath}
                  onFollow={(event): void => {
                    event.preventDefault();
                    navigate(createPath);
                  }}
                >
                  Create golden dataset
                </Button>
              )
            }
            description="Reference jobs with the cost each should have, priced by the cost impact of each change set to their models"
          >
            Golden datasets
          </Header>
        }
        empty={
          <Box textAlign="center" color="inherit">
            {loadErrors.length > 0 ? '' : 'No golden datasets.'}
          </Box>
        }
      />
    </SpaceBetween>
  );
};

GoldenDatasetsList.propTypes = {
  apiClient: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  canWrite: PropTypes.bool.isRequired,
  deletedName: PropTypes.string,
};

const GoldenDatasetsListView = (props: Omit<GoldenDatasetsListProps, 'apiClient'>): JSX.Element => (
  <GoldenDatasetsList apiClient={ApiClient} {...props} />
);

export default GoldenDatasetsListView;
